<script setup>
import { computed } from 'vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  processedParams: {
    type: Object,
    default: () => ({}),
  },
});

const headerComponent = computed(() =>
  props.template?.components?.find(component => component.type === 'HEADER')
);
const bodyComponent = computed(() =>
  props.template?.components?.find(component => component.type === 'BODY')
);
const footerComponent = computed(() =>
  props.template?.components?.find(component => component.type === 'FOOTER')
);
const buttonsComponent = computed(() =>
  props.template?.components?.find(component => component.type === 'BUTTONS')
);

const headerParams = computed(() => props.processedParams?.header || {});
const bodyParams = computed(() => props.processedParams?.body || {});
const buttonParams = computed(() => props.processedParams?.buttons || []);

const headerFormat = computed(() => headerComponent.value?.format);
const hasMediaHeader = computed(() =>
  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat.value)
);

const headerMediaPreview = computed(() => {
  if (!hasMediaHeader.value) return null;
  const mediaId = headerParams.value.media_id;
  const mediaUrl = headerParams.value.media_url;

  if (mediaUrl) {
    return { type: headerParams.value.media_type, url: mediaUrl };
  }

  if (mediaId) {
    return { type: headerParams.value.media_type, id: mediaId };
  }

  return null;
});

const replacePlaceholders = (text, params = {}) => {
  if (!text) return '';

  return text.replace(/{{(\d+)}}/g, (_, index) => {
    const key = index.toString();
    return params[key] ?? `{{${index}}}`;
  });
};

const bodyText = computed(() =>
  replacePlaceholders(bodyComponent.value?.text, bodyParams.value)
);

const headerText = computed(() =>
  replacePlaceholders(headerComponent.value?.text, headerParams.value)
);

const footerText = computed(() => footerComponent.value?.text || '');

const displayButtons = computed(() => {
  const buttons = buttonsComponent.value?.buttons || [];
  return buttons.map((button, index) => {
    const paramValue = buttonParams.value?.[index]?.parameter;
    const filledText = button.text?.replace(/{{(\d+)}}/g, paramValue || button.text);
    return {
      ...button,
      displayText: filledText || button.text,
    };
  });
});
</script>

<template>
  <div class="wa-template-preview">
    <div class="wa-bubble">
      <!-- Header -->
      <div v-if="headerComponent" class="wa-section">
        <div v-if="hasMediaHeader && headerMediaPreview" class="wa-header-media">
          <img
            v-if="headerMediaPreview.type === 'image' && headerMediaPreview.url"
            :src="headerMediaPreview.url"
            alt="Header media preview"
          />
          <video
            v-else-if="headerMediaPreview.type === 'video' && headerMediaPreview.url"
            :src="headerMediaPreview.url"
            controls
          />
          <div v-else class="wa-media-placeholder">
            Mídia via ID: {{ headerMediaPreview.id || 'n/d' }}
          </div>
        </div>
        <div v-else-if="headerText" class="wa-header-text">
          {{ headerText }}
        </div>
      </div>

      <!-- Body -->
      <div v-if="bodyComponent" class="wa-body" v-html="bodyText.replace(/\n/g, '<br>')" />

      <!-- Footer -->
      <div v-if="footerText" class="wa-footer">
        {{ footerText }}
      </div>

      <!-- Buttons -->
      <div v-if="displayButtons.length" class="wa-buttons">
        <button
          v-for="(button, idx) in displayButtons"
          :key="idx"
          class="wa-button"
          type="button"
        >
          {{ button.displayText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wa-template-preview {
  @apply w-full;
}

.wa-bubble {
  @apply bg-white dark:bg-n-slate-1 border border-n-weak rounded-2xl p-3 shadow-sm;
}

.wa-section + .wa-body,
.wa-section + .wa-footer,
.wa-section + .wa-buttons,
.wa-body + .wa-footer,
.wa-body + .wa-buttons,
.wa-footer + .wa-buttons {
  @apply mt-2;
}

.wa-header-text {
  @apply text-sm font-semibold text-n-slate-12;
}

.wa-body {
  @apply text-sm text-n-slate-12 whitespace-pre-line;
}

.wa-footer {
  @apply text-xs text-n-slate-10;
}

.wa-header-media {
  @apply overflow-hidden rounded-xl border border-n-weak;
}

.wa-header-media img,
.wa-header-media video {
  @apply w-full h-auto;
}

.wa-media-placeholder {
  @apply text-xs text-n-slate-10 bg-n-alpha-2 px-3 py-2;
}

.wa-buttons {
  @apply flex flex-wrap gap-2;
}

.wa-button {
  @apply px-3 py-1.5 text-sm rounded-full border border-n-weak bg-n-alpha-2 text-n-slate-12;
}
</style>
